import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory, useLocation } from "react-router-dom";
import useAxios from "../../../utils/useAxios";
import TournamentGroupsSelectPlayerModal from "./TournamentGroupsSelectPlayerModal";
import TournamentGroupsMatchesModal from "./TournamentGroupsMatchesModal";
import useTournamentSocket from "../../../hooks/useTournamentSocket";
import useFetchStages from "../../../hooks/useFetchStages";
import useViewMode from "../../../hooks/useViewMode";
import { useIsWideViewport } from "../../../hooks/useIsWideViewport";
import TournamentGroupTable from "./TournamentGroupTable";
import IconDropdown from "../../../components/IconDropdown";
import PageLoader from "../../../components/PageLoader";
import StageSelector from "../components/StageSelector";
import EmptyData from "../../EmptyData";
import { openTournamentSettings } from "../../../utils/tournamentSettingsBus";
import { publishTournamentSearchOptions } from "../../../utils/tournamentSearchOptionsBus";
import { fetchTeamRosterMap } from "../../../utils/fetchTeamRosterMap";
import { fetchPlayerNameMap } from "../../../utils/fetchPlayerNameMap";


const TournamentGroups = ({ tournament, is_authorized, hasGuestToken, stageControl, sheetTargetRef, viewMode: controlledViewMode, onViewModeChange, searchChips = [] }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const api = useAxios();

    const [groups, setGroups] = useState([]);
    // null = "Всі групи" (every group rendered together, the groups-grid
    // below) - a real switch, not an anchor: picking one group shows only
    // that group's own table, same idea as the placement draws' own
    // StageTabsScroll switch, not a scroll jump within an always-fully-
    // rendered page.
    const [activeGroupId, setActiveGroupId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState(null);
    // Wide viewports dock the participant-picker panel and the matches sheet
    // on the same right-edge slot (see ParticipantPickerPanel/BottomSheetPanel)
    // - only one can occupy it at a time, so the matches sheet below is
    // unmounted for as long as the picker is open there.
    const isWide = useIsWideViewport();
    const [participants, setParticipants] = useState([]);
    const [selectedMatchId, setSelectedMatchId] = useState(null);
    // Bumped (not just selectedMatchId, which may already equal the clicked
    // match) on every match click so the matches sheet always (re)expands,
    // even for a re-click on the same match.
    const [expandSignal, setExpandSignal] = useState(0);
    // The matches sheet only exists in the DOM while actually open now (see
    // BottomSheetPanel.jsx's own comment on why the old permanent peek was
    // removed) - sheetTargetRef survives this component being torn down and
    // rebuilt (the Draw tab swapping its whole Groups<->Grids subtree when
    // the selected stage crosses from a bracket stage to a group stage or
    // back), so a sheet the user had open stays open across that swap
    // instead of a fresh mount always defaulting closed. "full"/"peek" are
    // the same two sentinel strings the sheet's own drag-to-resize logic
    // used to write here before it lost its PEEK resting state - reused as
    // open/closed instead of introducing a third value just for this.
    const [sheetOpen, setSheetOpen] = useState(() => sheetTargetRef?.current === "full");
    useEffect(() => {
        if (sheetTargetRef) sheetTargetRef.current = sheetOpen ? "full" : "peek";
    }, [sheetOpen, sheetTargetRef]);
    // Closes the participant picker (if open) same as its own onClose does -
    // both panels dock on the same right-edge slot, and since the picker no
    // longer auto-closes after a pick (it stays open across several adds in
    // a row), it would otherwise keep hogging that slot forever, leaving no
    // way back to the matches sheet short of the picker's own tiny X.
    // Clicking any match now always wins the slot back for it instead.
    // useCallback (here and on the handlers below that pass down into
    // TournamentGroupTable as props) - it's now React.memo'd, so a plain
    // function const recreated fresh on every TournamentGroups render would
    // silently defeat that: memo's shallow prop comparison sees a "changed"
    // function prop every time regardless of whether anything relevant
    // actually changed, forcing every mounted instance (every group's whole
    // table) to re-render anyway. Same reasoning as Bracket.js/
    // BracketClassic.js's own identical pass.
    const expandMatch = useCallback((matchId) => {
        setSelectedMatchId(matchId);
        setExpandSignal(n => n + 1);
        setSheetOpen(true);
        setModalOpen(false);
        setPickedCandidate(null);
    }, []);
    const closeMatchesSheet = () => setSheetOpen(false);
    const [pickedPlayer, setPickedPlayer] = useState(null);
    // A candidate picked from the still-open picker panel (see
    // TournamentGroupsSelectPlayerModal.js's own hasTarget branch) rather
    // than from a specific "+"-targeted slot - same "Drop here"/crosshair
    // treatment as pickedPlayer's move-mechanic, just placing a NEW
    // participant instead of moving an already-seated one. Mutually
    // exclusive with pickedPlayer (see the guards below).
    const [pickedCandidate, setPickedCandidate] = useState(null);
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const [loadingCells, setLoadingCells] = useState({});
    const longPressTimer = useRef(null);
    // Guards fetchGroups/fetchParticipants against out-of-order responses:
    // every add/move/delete on a group cell fires its own fetchGroups() +
    // fetchParticipants() to resync with the server, with no cancellation
    // between them - firing several in quick succession (repeated add/
    // remove) doesn't guarantee the responses land in the same order the
    // requests were sent in, so a slower, older response could resolve
    // last and clobber newer state with stale data (e.g. an already-freed
    // participant reappearing as "occupied" and permanently unselectable
    // in the picker, until the next full refetch happens to win the
    // race). Only the response from the most recently *fired* request of
    // each kind is applied - see fetchGroups/fetchParticipants below.
    const groupsReqRef = useRef(0);
    const participantsReqRef = useRef(0);
    // Scrolls the first highlighted row into view - queried off the DOM
    // (searchStateClass's own ".search-match", set per-row by each
    // TournamentGroupTable instance below) rather than tracked through
    // props, since there can be several group tables rendered here at once
    // and this only needs to run once per distinct match regardless of
    // which one it's in. Same technique as TournamentPlayers.js's own
    // version of this effect.
    const lastScrolledMatchRef = useRef(null);
    useEffect(() => {
        if (searchChips.length === 0) {
            lastScrolledMatchRef.current = null;
            return;
        }
        const match = document.querySelector("#groups-block .search-match");
        if (match && match !== lastScrolledMatchRef.current) {
            lastScrolledMatchRef.current = match;
            match.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [searchChips, participants]);

    useEffect(() => {
        const onTouch = () => setIsTouchDevice(true);
        window.addEventListener("touchstart", onTouch, { once: true });
        return () => window.removeEventListener("touchstart", onTouch);
    }, []);

    // Same controlled/standalone split as TournamentGrids.js's own
    // Classic/Pro toggle: the Матриця/Залік toggle always renders in the
    // group-tabs row below, for both standalone and merged (stageControl
    // set, the Жеребкування flow via TournamentDrawStages) use. When
    // controlled, the *state* it flips is owned by that parent
    // (controlledViewMode/onViewModeChange - its own "groups_view_mode"
    // useViewMode instance) - this own hook still runs (rules of hooks) but
    // its value/setter are only used standalone.
    const ownViewMode = useViewMode("groups_view_mode", "matrix");
    const viewMode = stageControl ? controlledViewMode : ownViewMode.viewMode;
    const toggleViewMode = stageControl ? onViewModeChange : ownViewMode.toggleViewMode;

    const ownFetchStages = useFetchStages(
        api, `/api/v1.0/tournaments/${id}/group-stage/`
    );
    const { stages, activeStage, setActiveStage, isLoading: isStagesLoading } = stageControl || ownFetchStages;

    useEffect(() => {
        if (!stageControl) ownFetchStages.fetchStages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Starts true (not false) - once useFetchStages resolves, it commits
    // stages/activeStage/isStagesLoading:false all in the same render, but
    // this component's own groups-loading effect (which flips this back to
    // true for the actual groups fetch) only runs *after* that render
    // commits. Starting false left a one-frame window where isLoading was
    // false and groups was still [] - EmptyData's "No groups created"
    // flashed on screen before the real fetch/loader took over.
    const [isGroupsLoading, setIsGroupsLoading] = useState(true);

    useTournamentSocket(tournament?.id, (payload) => {
        if (payload?.wake_up) {
            // Routed through fetchGroups (not a standalone api.get) so it
            // shares groupsReqRef's race guard - the backend broadcasts this
            // wake_up to every connected client, including whoever just made
            // the change themselves, so it routinely lands while THEIR OWN
            // add/delete is still resolving its own fetchGroups() call. Two
            // independent, unguarded fetches racing like that is exactly
            // what produced the "participant reappears, then vanishes again"
            // flicker - whichever of the two responses happened to resolve
            // last silently won, regardless of which one was actually newer.
            if (activeStage) {
                fetchGroups(activeStage);
                if (tournament?.can_edit && is_authorized) {
                    fetchParticipants(activeStage);
                }
            }
            return;
        }

        if (activeStage && payload?.updated_matches) {
            updateMatches(payload.updated_matches);
        }
    });

    useEffect(() => {
        if (isStagesLoading) return;
        // A group id picked under the previous stage means nothing here -
        // back to "Всі групи" every time the stage itself changes.
        setActiveGroupId(null);

        if (stages.length === 0) {
            setGroups([]);
            setParticipants([]);
            setIsGroupsLoading(false);
            return;
        }

        // Stages exist but activeStage hasn't been resolved from the URL yet
        // (set by a sibling effect, not visible here until the next render) -
        // stay in the loading state instead of flashing "no groups" for that
        // one render before activeStage lands. Same fix as TournamentGrids.js.
        if (!activeStage) return;

        // Routed through fetchGroups (not a standalone api.get) so this
        // stage-mount fetch shares groupsReqRef's race guard too - without
        // it, switching stages and immediately adding/deleting a participant
        // could race this fetch against fetchGroups's own, with whichever
        // response happened to resolve last silently winning regardless of
        // which one was actually newer.
        let isMounted = true;
        setIsGroupsLoading(true);

        fetchGroups(activeStage).finally(() => {
            if (isMounted) setIsGroupsLoading(false);
        });

        return () => { isMounted = false; };
    }, [activeStage, stages.length, isStagesLoading, id]);

    const updateMatches = useCallback((updatedMatches) => {
        if (!updatedMatches?.length) return;
        const map = Object.fromEntries(updatedMatches.map(m => [m.id, m]));

        setGroups(prev => prev.map(group => {
            // skip groups with nothing in this batch of updates - otherwise
            // every group (including ones untouched by this push) gets a
            // brand new object reference, which invalidates React.memo on
            // every mounted TournamentGroupTable instance and forces a full
            // re-render of each. Same fix as TournamentGrids.js's own
            // updateMatches, which already explains the "sluggish on rapid
            // updates" symptom this avoids.
            if (!group.matches.some(m => map[m.id])) return group;

            const mergedMatches = group.matches.map(match => {
                const upd = map[match.id];
                if (!upd) return match;

                let mergedSets = match.sets;
                if (upd.sets) {
                    const setsMap = {};
                    (match.sets || []).forEach((s, i) => {
                        if (s) {
                            const num = s.number !== undefined ? s.number : i + 1;
                            setsMap[num] = s;
                        }
                    });
                    upd.sets.forEach((s, i) => {
                        if (s) {
                            const num = s.number !== undefined ? s.number : i + 1;
                            setsMap[num] = { ...(setsMap[num] || {}), ...s };
                        }
                    });
                    const res = Object.values(setsMap).sort((a, b) => a.number - b.number);
                    mergedSets = res.length > 0 ? res : upd.sets;
                }

                return {
                    ...match,
                    ...upd,
                    sets: mergedSets
                };
            });

            // A touched group with no players array yet still needs its
            // matches merged, just not the standings recalc below (nothing
            // to recalc onto).
            if (!group.players) {
                return { ...group, matches: mergedMatches };
            }

            // Mirrors the backend's own group-stage standings calc (total game
            // difference across completed matches, 1pt per win) so the +/- and
            // PTS columns move live off the socket push itself, for every
            // connected client, without waiting on a full groups refetch.
            const updatedPlayers = group.players.map(p => p ? { ...p, games: 0, points: 0 } : null);
            let matchIndex = 0;
            for (let r = 0; r < group.number_players; r++) {
                for (let c = r + 1; c < group.number_players; c++) {
                    const match = mergedMatches[matchIndex++];
                    if (match && match.status === 7) {
                        let g1 = 0, g2 = 0;
                        (match.sets || []).forEach(s => {
                            g1 += s.game_score_1 || 0;
                            g2 += s.game_score_2 || 0;
                        });
                        if (updatedPlayers[r]) {
                            updatedPlayers[r].games += (g1 - g2);
                            updatedPlayers[r].points += (match.winner_side === 1 ? 1 : (g1 > g2 ? 1 : 0));
                        }
                        if (updatedPlayers[c]) {
                            updatedPlayers[c].games += (g2 - g1);
                            updatedPlayers[c].points += (match.winner_side === 2 ? 1 : (g2 > g1 ? 1 : 0));
                        }
                    }
                }
            }

            return { ...group, matches: mergedMatches, players: updatedPlayers };
        }));
    }, []);

    useEffect(() => {
        if (activeStage && tournament?.can_edit && is_authorized) {
            fetchParticipants(activeStage);
        }
    }, [activeStage, tournament?.can_edit, is_authorized]);

    const fetchGroups = useCallback(async (stageId, search = "") => {
        groupsReqRef.current += 1;
        const currentReq = groupsReqRef.current;
        try {
            const res = await api.get(`/api/v1.0/group-stage/${stageId}/groups/?limit=100`, { params: { search } });
            if (groupsReqRef.current !== currentReq) return;
            const mappedGroups = (res.data.results || []).map(group => ({
                ...group,
                players: Array.from({ length: group.number_players }, (_, i) => group.participants?.[i] || null),
                matches: group.matches || []
            }));
            setGroups(mappedGroups);
        } catch (e) {
            console.error(e);
        }
    }, [api]);

    const fetchParticipants = useCallback(async (stageId) => {
        participantsReqRef.current += 1;
        const currentReq = participantsReqRef.current;
        try {
            const res = await api.get(`/api/v1.0/group-stage/${stageId}/participants/?limit=100`);
            if (participantsReqRef.current !== currentReq) return;
            setParticipants(res.data.results || []);
        } catch (e) {
            console.error(e);
        }
    }, [api]);

    const handleAddToCell = useCallback((groupId, position, participant) => {
        let replacedParticipant = null;
        setGroups(prev => prev.map(group => {
            if (group.id !== groupId) return group;
            const players = [...(group.players || [])];
            replacedParticipant = players[position - 1];
            players[position - 1] = participant;
            return { ...group, players };
        }));

        setParticipants(prev => {
            const pId = participant.player_id || participant.id || participant.team_id;
            let updated = prev.filter(p => {
                const curId = p.player_id || p.id || p.team_id;
                return String(curId) !== String(pId);
            });
            if (replacedParticipant) {
                const repId = replacedParticipant.player_id || replacedParticipant.id || replacedParticipant.team_id;
                const exists = updated.some(p => {
                    const curId = p.player_id || p.id || p.team_id;
                    return String(curId) === String(repId);
                });
                if (!exists) updated = [...updated, replacedParticipant];
            }
            return updated;
        });

        if (activeStage) {
            fetchParticipants(activeStage);
            fetchGroups(activeStage);
        }
    }, [activeStage, fetchParticipants, fetchGroups]);

    // The reverse flow: a candidate was picked from the still-open panel
    // (no specific slot targeted yet - see pickedCandidate above) and the
    // user just tapped an empty cell to place them there. Unlike
    // handleAddToCell (optimistic-update only, called from inside the
    // modal's own handleSelect after ITS OWN POST resolves), this is the
    // destination click happening in a sibling component - the modal isn't
    // on the call stack, so this does the request itself too.
    // No loadingCells wrapping here (unlike the modal's own targeted add) -
    // mirrors Grids' own handlePlaceCandidateInMatch, which doesn't show a
    // per-slot loading state for this flow either: the tapped cell is
    // showing "Перемістити" right up to the tap, and swapping that
    // straight to the loading-dots spinner (rather than back to "+
    // Учасник" once the pick concludes, the same way it was before being
    // picked) read as a jarring extra step, not useful feedback.
    const handlePlaceCandidate = useCallback(async (groupId, position) => {
        if (!pickedCandidate) return;
        const participant = pickedCandidate;
        try {
            const payload = tournament?.grade === 1
                ? { position, player_id: participant.player_id || participant.id }
                : { position, team_id: participant.team_id || participant.id };
            await api.post(`/api/v1.0/group-stage/${activeStage}/groups/${groupId}/players/`, payload);
            handleAddToCell(groupId, position, participant);
            setPickedCandidate(null);
        } catch (e) {
            console.error("Failed to place participant:", e);
        }
    }, [pickedCandidate, tournament?.grade, api, activeStage, handleAddToCell]);

    // Tapping the already-picked candidate again cancels the pick, same
    // "tap source again to cancel" idea handleCellTap already uses for
    // pickedPlayer.
    const getCandidateId = (p) => p?.player_id || p?.id || p?.team_id;
    const handlePickCandidate = (participant) => {
        setPickedCandidate(prev => (prev && getCandidateId(prev) === getCandidateId(participant)) ? null : participant);
    };

    const getParticipantId = (participant) =>
        participant?.id ?? (tournament?.grade === 1 ? participant?.player_id : participant?.team_id);

    const movePlayer = useCallback(async (src, target) => {
        if (!src || !target) return;
        if (src.groupId === target.groupId && src.position === target.position) return;

        const srcGroup = groups.find(g => g.id === src.groupId);
        const targetGroup = groups.find(g => g.id === target.groupId);
        if (!srcGroup || !targetGroup) return;

        const srcParticipant = srcGroup.players?.[src.position - 1];
        const targetParticipant = targetGroup.players?.[target.position - 1];
        if (!srcParticipant) return;

        setGroups(prev => prev.map(g => {
            if (g.id === src.groupId && g.id === target.groupId) {
                const players = [...g.players];
                players[src.position - 1] = targetParticipant || null;
                players[target.position - 1] = srcParticipant;
                return { ...g, players };
            }
            if (g.id === src.groupId) {
                const players = [...g.players];
                players[src.position - 1] = targetParticipant || null;
                return { ...g, players };
            }
            if (g.id === target.groupId) {
                const players = [...g.players];
                players[target.position - 1] = srcParticipant;
                return { ...g, players };
            }
            return g;
        }));

        try {
            const srcId = getParticipantId(srcParticipant);
            await api.delete(`/api/v1.0/group-stage/${activeStage}/groups/${src.groupId}/players/${srcId}/`);

            if (targetParticipant) {
                const targetId = getParticipantId(targetParticipant);
                await api.delete(`/api/v1.0/group-stage/${activeStage}/groups/${target.groupId}/players/${targetId}/`);
            }

            const srcPayload = tournament?.grade === 1
                ? { position: target.position, player_id: srcParticipant.player_id || srcParticipant.id }
                : { position: target.position, team_id: srcParticipant.team_id || srcParticipant.id };

            await api.post(`/api/v1.0/group-stage/${activeStage}/groups/${target.groupId}/players/`, srcPayload);

            if (targetParticipant) {
                const targetPayload = tournament?.grade === 1
                    ? { position: src.position, player_id: targetParticipant.player_id || targetParticipant.id }
                    : { position: src.position, team_id: targetParticipant.team_id || targetParticipant.id };

                await api.post(`/api/v1.0/group-stage/${activeStage}/groups/${src.groupId}/players/`, targetPayload);
            }

            fetchGroups(activeStage);
            fetchParticipants(activeStage);
        } catch (e) {
            console.error("Failed to move player:", e);
            fetchGroups(activeStage);
            fetchParticipants(activeStage);
        }
    }, [groups, activeStage, tournament?.grade, api, fetchGroups, fetchParticipants]);

    const handlePlayerTouchStart = useCallback((e, groupId, position, participant) => {
        if (!is_authorized) return;
        if (pickedPlayer) return;
        // Can't start moving an existing participant while the add-picker
        // panel actually has something active - a specific slot targeted
        // (modalData, right after a "+" click) or a candidate picked
        // (pickedCandidate) - the two "picking" systems would fight over
        // the same visual language (crosshair cursor, "Drop here")
        // otherwise. The panel merely being open and idle (docked, nothing
        // targeted/picked) isn't itself a conflict - same condition
        // TournamentGroupTable.js's own draggable/onTouchStart guards
        // already use, this just matches it instead of the broader (and
        // wrong) "panel is open at all" check that used to be here.
        if (isWide && (modalData || pickedCandidate)) return;

        clearTimeout(longPressTimer.current);
        const touch = e.touches[0];
        const startX = touch.clientX;
        const startY = touch.clientY;

        const timer = setTimeout(() => {
            if (navigator.vibrate) navigator.vibrate(50);
            setPickedPlayer({ groupId, position, participant });
        }, 400);

        longPressTimer.current = timer;
        const target = e.currentTarget;

        const checkMove = (eMove) => {
            const touchMove = eMove.touches[0];
            if (Math.abs(touchMove.clientX - startX) > 10 || Math.abs(touchMove.clientY - startY) > 10) {
                clearTimeout(longPressTimer.current);
            }
        };

        const cleanup = () => {
            clearTimeout(longPressTimer.current);
            target.removeEventListener("touchmove", checkMove);
            target.removeEventListener("touchend", cleanup);
            target.removeEventListener("touchcancel", cleanup);
        };

        target.addEventListener("touchmove", checkMove, { passive: true });
        target.addEventListener("touchend", cleanup, { once: true });
        target.addEventListener("touchcancel", cleanup, { once: true });
    }, [is_authorized, pickedPlayer, isWide, modalData, pickedCandidate]);

    const handleCellTap = useCallback((groupId, position) => {
        if (!is_authorized || !pickedPlayer) return;

        const src = pickedPlayer;
        setPickedPlayer(null);

        // tapping the source cell again cancels the pick instead of moving it
        if (src.groupId === groupId && src.position === position) return;
        movePlayer(src, { groupId, position });
    }, [is_authorized, pickedPlayer, movePlayer]);

    const handleDragStartPlayer = useCallback((e, groupId, position) => {
        if (!is_authorized) return;
        e.dataTransfer.setData("groupId", String(groupId));
        e.dataTransfer.setData("position", String(position));
        e.dataTransfer.effectAllowed = "move";
    }, [is_authorized]);

    const handleDropOnCell = useCallback((e, groupId, position) => {
        if (!is_authorized) return;
        e.preventDefault();
        const srcGroupIdRaw = e.dataTransfer.getData("groupId");
        const srcPosition = Number(e.dataTransfer.getData("position"));
        if (!srcGroupIdRaw) return;

        const srcGroup = groups.find(g => String(g.id) === srcGroupIdRaw);
        if (!srcGroup) return;

        movePlayer({ groupId: srcGroup.id, position: srcPosition }, { groupId, position });
    }, [is_authorized, groups, movePlayer]);

    // Stable (useCallback) versions of what used to be inline arrow props on
    // TournamentGroupTable below - it's now React.memo'd, and a fresh inline
    // `() => ...` on every render defeats that the same way a plain
    // function const would (see fetchGroups' own comment above).
    const handleCancelCandidate = useCallback(() => setPickedCandidate(null), []);
    const handleStartMove = useCallback((groupId, position, participant) => setPickedPlayer({ groupId, position, participant }), []);

    const occupiedParticipants = useMemo(() => {
        const list = [];
        (groups || []).forEach(g => {
            (g.players || []).forEach(p => {
                if (p) list.push(p);
            });
        });
        return list;
    }, [groups]);

    // Same idea as TournamentGrids.js's own drawsToRender - keeps the
    // persistent matches sheet in sync with whichever group tab is active,
    // instead of always listing every group's matches regardless of the
    // "Всі групи"/single-group selection above.
    const groupsToRender = activeGroupId === null ? groups : groups.filter(g => g.id === activeGroupId);

    // Only set when this stage is a dedicated "playoff for places" stage
    // (configured in TournamentGroupsSettings) - regular qualifying-round
    // group stages have no meaningful final place to assign, so this stays
    // null for them and the per-participant place picker stays hidden.
    const startPlace = stages.find(s => s.id === activeStage)?.start_place ?? null;
    const maxPlayers = groups.length > 0 ? Math.max(...groups.map(g => g.number_players || 3)) : 4;
    const minGridWidth = Math.max(480, 240 + (maxPlayers * 80));
    const isLoading = isStagesLoading || isGroupsLoading;

    // Team (grade 3) group rows only carry the team's own name, not its
    // player roster - see fetchTeamRosterMap.js's own comment. Fetched once
    // per tournament (not per group/stage - a team's roster doesn't change
    // between stages) so a "player" search chip can still match a team by
    // any of its players, not just its own name.
    const [teamRoster, setTeamRoster] = useState(new Map());
    useEffect(() => {
        if (tournament?.grade !== 3 || !tournament?.player_quantity) return;
        let cancelled = false;
        fetchTeamRosterMap(api, id, tournament.player_quantity).then(map => {
            if (!cancelled) setTeamRoster(map);
        });
        return () => { cancelled = true; };
        // api excluded on purpose - useAxios() returns a new instance every
        // render, so listing it here re-ran this fetch (and its setTeamRoster,
        // which itself triggers a render) in a tight infinite loop instead of
        // the intended once-per-tournament fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, tournament?.grade, tournament?.player_quantity]);

    // Singles/doubles (grade 1/2): group rows only carry each player's
    // first_name truncated to an initial (see fetchPlayerNameMap.js's own
    // comment) - fetched once per tournament, same shape/reasoning as
    // teamRoster above, so a full-first-name search chip still matches.
    const [playerNames, setPlayerNames] = useState(new Map());
    useEffect(() => {
        if (tournament?.grade === 3 || !tournament?.player_quantity) return;
        let cancelled = false;
        fetchPlayerNameMap(api, id, tournament.player_quantity).then(map => {
            if (!cancelled) setPlayerNames(map);
        });
        return () => { cancelled = true; };
        // api excluded - see teamRoster's identical comment above.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, tournament?.grade, tournament?.player_quantity]);

    // Same label text as the group-tabs row below (keyed by group.number,
    // TournamentGroupTable's own chip-matching key) - shared so the global
    // search box's "group" chip options read identically to the tab you'd
    // actually click to see the same group.
    const groupLabels = useMemo(() => {
        const map = new Map();
        let placeCursor = startPlace;
        groups.forEach(group => {
            const groupStartPlace = placeCursor;
            if (startPlace) placeCursor += group.number_players || 0;
            const label = startPlace
                ? `${groupStartPlace}-${groupStartPlace + (group.number_players || 1) - 1}`
                : String.fromCharCode(64 + group.number);
            map.set(group.number, label);
        });
        return map;
    }, [groups, startPlace]);

    // Group + player-name chip options for the global search box - see
    // tournamentSearchOptionsBus's own comment for why this lives here
    // instead of in Tournament.js, which owns the search UI but not this
    // tab's data.
    useEffect(() => {
        const names = new Map();
        // pl.first_name here is a single-letter initial (see playerNames'
        // own comment above) - resolve the real one by id before building
        // the suggestion label, so selecting it actually matches every row.
        const addName = (pl) => {
            if (!pl?.last_name) return;
            const resolved = playerNames.get(pl.id) || pl;
            const full = `${resolved.last_name} ${resolved.first_name || ''}`.trim();
            names.set(full, full);
        };
        groups.forEach(group => {
            (group.players || []).forEach(p => {
                if (!p) return;
                if (tournament?.grade === 3) {
                    if (p.name) names.set(p.name, p.name);
                    (teamRoster.get(p.name) || []).forEach(addName);
                } else {
                    (tournament?.grade === 2 ? (p.players || []) : [p]).forEach(addName);
                }
            });
        });
        const options = [];
        if (groups.length > 0) {
            options.push({
                label: t("Groups"),
                options: groups.map(group => ({
                    value: `group_${group.id}`,
                    label: groupLabels.get(group.number),
                    type: 'group',
                    realValue: group.number
                }))
            });
        }
        if (names.size > 0) {
            options.push({ label: t("Players"), options: Array.from(names.values()).map(n => ({ value: `p_${n}`, label: n, type: 'player' })) });
        }
        publishTournamentSearchOptions(options);
    }, [groups, groupLabels, tournament?.grade, teamRoster, playerNames, t]);

    return (
        <>
            {(pickedPlayer || pickedCandidate) && (
                <div
                    onClick={() => { setPickedPlayer(null); setPickedCandidate(null); }}
                    style={{
                        position: "fixed",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        backgroundColor: "#111827",
                        color: "white",
                        padding: "10px 20px",
                        borderRadius: "20px",
                        zIndex: 100,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        width: "fit-content",
                        cursor: "pointer",
                        userSelect: "none"
                    }}
                >
                    <span style={{ fontSize: "14px", fontWeight: "500", whiteSpace: "nowrap" }}>{t("Tap destination cell")}</span>
                    <FeatherIcon icon="x" size={16} />
                </div>
            )}
            {/* Matrix/Standings toggle moved down to the group-tabs row below
                (same "at the place/group-tabs level" spot TournamentGrids
                uses for its own Classic/Pro toggle) - this row is now just
                the stage picker. */}
            {!stageControl && stages.length > 0 && (
                <ul className="tabs" style={{ marginTop: "10px", marginBottom: "10px" }}>
                    <StageSelector
                        stages={stages}
                        activeStage={activeStage}
                        onStageChange={setActiveStage}
                    />
                </ul>
            )}

            {/* Real switch, same idea as the placement draws' own
                StageTabsScroll - picking a group shows only that group's
                table (below), the rest hidden rather than everyone
                scrolling past them. Matrix/Standings toggle now lives here
                too (not the outer stage-tabs row above/on TournamentDraw
                Stages) - same "at the place/group-tabs row" level as
                TournamentGrids' own Classic/Pro toggle next to its
                grid-tab-item row, for both standalone and merged
                (stageControl) use. Row itself renders whenever there's at
                least one group (not just >1 - mirrors TournamentGrids'
                .tournament-stages, which always has at least the "main"
                tab) so the toggle isn't lost on a single-group stage; the
                group-switcher <ul> itself still only appears once there's
                actually more than one to switch between. */}
            {/* Same <ul class="tabs">/wrapping-<li>/IconDropdown-as-sibling-<li>
                shape as TournamentGrids' own .tournament-stages row (grid-
                tabs-wrap/grid-tabs-scroll) - the icon needs a real .tabs
                ancestor to pick up .tabs a.action-button's own look (padding/
                color/etc.) and .tabs li.right's margin-left:auto; the plain
                <div> this used before wasn't one, so the icon rendered as a
                bare unstyled link instead of matching Grids' button. */}
            {groups.length > 0 && (
                <ul className="tabs group-tabs-row" style={{ marginTop: "10px", marginBottom: "10px" }}>
                    {groups.length > 0 && (
                        <li className="tournament-header-tabs-wrap" style={{ flex: "1 1 auto", minWidth: 0 }}>
                            <ul className="tournament-header-tabs tournament-subtoolbar-inset">
                                {groups.length > 1 && (
                                    <li className={activeGroupId === null ? "active" : ""}>
                                        <a href="#" onClick={(e) => { e.preventDefault(); setActiveGroupId(null); }}>
                                            {t("All groups")}
                                        </a>
                                    </li>
                                )}
                                {groups.map(group => (
                                    // groups.length===1: "All groups" toggle above is hidden then, but
                                    // activeGroupId still defaults to null (not this group's id) - without
                                    // the length check the lone tab rendered unstyled/inactive even though
                                    // it's the only (and effectively selected) one.
                                    <li key={group.id} className={(activeGroupId === group.id || groups.length === 1) ? "active" : ""}>
                                        <a href="#" onClick={(e) => { e.preventDefault(); setActiveGroupId(group.id); }}>
                                            {groupLabels.get(group.number)}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )}
                    <IconDropdown
                        activeId={viewMode}
                        onChange={toggleViewMode}
                        className="action-button matches-btn"
                        options={[
                            { id: "matrix", icon: "grid", label: "Matrix" },
                            { id: "standings", icon: "list", label: "Standings" }
                        ]}
                    />
                </ul>
            )}

            {isLoading ? (
                <PageLoader />
                // <GroupSkeleton grade={tournament?.grade} count={groups.length > 0 ? groups.length : 2} is_authorized={tournament?.can_edit && location?.pathname?.startsWith("/admin")} />
            ) : ((groups.length > 0 && (
                <div id="groups-block">
                    <div
                        className="groups-grid"
                        style={{
                            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${minGridWidth}px), 1fr))`
                        }}
                    >
                    {groups.map((group, index) => {
                        // Computed off the full groups array regardless of
                        // which one is actually rendered below - match
                        // numbering has to stay identical whether "Всі
                        // групи" or one specific group is showing, not
                        // restart from 1 just because this is now the only
                        // one on screen.
                        const matchOffset = groups
                            .slice(0, index)
                            .reduce((sum, g) => sum + (g.number_players * (g.number_players - 1)) / 2, 0);

                        if (activeGroupId !== null && activeGroupId !== group.id) return null;

                        return (
                            <TournamentGroupTable
                                key={group.id}
                                group={group}
                                tournament={tournament}
                                tournamentId={id}
                                is_authorized={is_authorized}
                                activeStage={activeStage}
                                stages={stages}
                                startPlace={startPlace}
                                participants={participants}
                                setGroups={setGroups}
                                fetchParticipants={fetchParticipants}
                                fetchGroups={fetchGroups}
                                setModalData={setModalData}
                                setModalOpen={setModalOpen}
                                setSelectedMatchId={setSelectedMatchId}
                                expandMatch={expandMatch}
                                matchOffset={matchOffset}
                                t={t}
                                history={history}
                                location={location}
                                pickedPlayer={pickedPlayer}
                                pickerTarget={isWide && modalOpen ? modalData : null}
                                pickedCandidate={pickedCandidate}
                                onPlaceCandidate={handlePlaceCandidate}
                                onCancelCandidate={handleCancelCandidate}
                                isTouchDevice={isTouchDevice}
                                onPlayerTouchStart={handlePlayerTouchStart}
                                onStartMove={handleStartMove}
                                onCellTap={handleCellTap}
                                onDragStartPlayer={handleDragStartPlayer}
                                onDropOnCell={handleDropOnCell}
                                loadingCells={loadingCells}
                                searchChips={searchChips}
                                teamRoster={teamRoster}
                                playerNames={playerNames}
                                viewMode={viewMode}
                            />
                        );
                    })}
                    </div>
                </div>
            )) || (
                <EmptyData
                    title="No groups created"
                    text="Groups have not been created yet. Data will appear here once the stage is generated."
                    actionLabel="Go to settings"
                    onAction={is_authorized ? () => openTournamentSettings("draw-stages") : undefined}
                />
            ))
            }

            <TournamentGroupsSelectPlayerModal
                {...modalData}
                onAddToCell={handleAddToCell}
                setLoadingCells={setLoadingCells}
                tournamentGrade={tournament?.grade}
                rating={tournament?.rating}
                participants={participants}
                occupiedParticipants={occupiedParticipants}
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    // Closing used to leave a still-unplaced pick stuck on -
                    // the panel was the only place to cancel it (tap the same
                    // candidate again), so closing it lost that and every
                    // click on an occupied cell underneath kept silently
                    // no-oping (see TournamentGroupTable.js's own
                    // onCancelCandidate). Closing the panel now cancels the
                    // pick too, same as clicking through to an occupied cell
                    // already does.
                    setPickedCandidate(null);
                }}
                // Clears the just-consumed target (not the panel itself,
                // which stays open on wide) - the FIRST pick after a "+"
                // click still fills that exact slot instantly; every pick
                // after that has no target, which is what flips the modal's
                // own candidate-click into "just pick, wait for a cell tap"
                // instead of posting immediately (see pickedCandidate above).
                onSuccess={() => setModalData(null)}
                pickedCandidate={pickedCandidate}
                onPickCandidate={handlePickCandidate}
            />
            <TournamentGroupsMatchesModal
                // Not gated on groups.length>0 anymore - same fix as
                // TournamentGrids.js's own isOpen, same reason: sheetOpen can
                // only turn true from an actual match click (groups already
                // exist then) or from sheetTargetRef inheriting "already
                // open" across TournamentDrawStages' Groups<->Grids swap. In
                // that swap case `groups` here starts at [] until this
                // stage's own fetch resolves - gating on its length made the
                // sheet disappear for that round-trip and then snap back
                // open once data landed, instead of just staying open.
                isOpen={sheetOpen && !(isWide && modalOpen)}
                onClose={closeMatchesSheet}
                expandSignal={expandSignal}
                fetchGroups={(search) => fetchGroups(activeStage, search)}
                groups={groupsToRender}
                setGroups={setGroups}
                tournamentId={tournament?.id}
                tournamentGrade={tournament?.grade}
                selectedMatchId={selectedMatchId}
                setSelectedMatchId={setSelectedMatchId}
                stages={stages}
                activeStage={activeStage}
                setActiveStage={setActiveStage}
                is_authorized={is_authorized || hasGuestToken}
                searchChips={searchChips}
                teamRoster={teamRoster}
                playerNames={playerNames}
            />
        </>
    );
};

export default TournamentGroups;
